import { type LucideIcon, ReceiptIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { VehicleCosts } from "@/features/vehicle-management/costs";
import { HistoryExtra } from "@/features/vehicle-management/history-extra";
import { SupplierServiceSwitch } from "@/features/vehicle-management/supplier-service-switch";
import { WorkOrderSection } from "@/features/vehicle-management/work-order-section";
import { useModule } from "@/lib/modules";

/**
 * The slots where modules add to the base app's pages (extensibility-review.md §3.6). The base
 * app imports a module only through this file (a lint rule enforces it); each slot renders
 * nothing while no module fills it, and a module's own components check `useModule()`, so the
 * base pages never ask which modules are active.
 */

export interface ModuleNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

export interface ModuleTab {
  value: string;
  label: ReactNode;
  content: ReactNode;
}

/** Menu entries modules add to the workshop group, after the base app's. */
export function useModuleNav(): ModuleNavItem[] {
  const { t } = useTranslation();
  const vehicleManagement = useModule("vehicleManagement");
  return vehicleManagement
    ? [{ to: "/costs", label: t("vehicleManagement.costs"), icon: ReceiptIcon }]
    : [];
}

/** Tabs modules add to a vehicle's page, after Details. */
export function useVehicleTabs(vehicleId: string): ModuleTab[] {
  const { t } = useTranslation();
  const vehicleManagement = useModule("vehicleManagement");
  return vehicleManagement
    ? [
        {
          value: "costs",
          label: t("vehicleManagement.costs"),
          content: <VehicleCosts vehicleId={vehicleId} />,
        },
      ]
    : [];
}

/** Cards modules add to a work order's side column, below its cost. */
export function WorkOrderSections({ workOrderId }: { workOrderId: string }): ReactNode {
  return <WorkOrderSection workOrderId={workOrderId} />;
}

/** A line modules add to a service history entry, below its type and parts. */
export function ServiceHistoryEntryExtra({
  vehicleId,
  workOrderId,
}: {
  vehicleId: string;
  workOrderId: string;
}): ReactNode {
  return <HistoryExtra vehicleId={vehicleId} workOrderId={workOrderId} />;
}

/** Fields modules add to an existing supplier's dialog; they save on their own. */
export function SupplierExtra({ supplierId }: { supplierId: string }): ReactNode {
  return <SupplierServiceSwitch supplierId={supplierId} />;
}
